import { createContext, useContext, useEffect, useState } from 'react';
import { api } from '../api/client.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount, check if the httpOnly cookie is valid by calling /api/me.
  // The cookie flows automatically — JS never reads it.
  useEffect(() => {
    api.get('/api/me')
      .then((r) => setUser(r.data.user))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  // Auth.js v5 requires a POST with CSRF token to start sign-in.
  // We fetch the CSRF token, then submit a hidden form to /signin/google.
  // After Google completes, Auth.js redirects to /api/auth/callback/google,
  // then to backend GET '/' which sets the launcher cookie and redirects to '/'.
  const signInWithGoogle = async () => {
    const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:4000';

    const csrfRes = await fetch(`${apiBase}/api/auth/csrf`, {
      credentials: 'include'
    });
    const { csrfToken } = await csrfRes.json();

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = `${apiBase}/api/auth/signin/google`;
    form.autocomplete = 'off';

    const csrfInput = document.createElement('input');
    csrfInput.type = 'hidden';
    csrfInput.name = 'csrfToken';
    csrfInput.value = csrfToken;
    form.appendChild(csrfInput);

    // Backend's GET / handler sets the cookie and forwards to the dashboard.
    const callbackInput = document.createElement('input');
    callbackInput.type = 'hidden';
    callbackInput.name = 'callbackUrl';
    callbackInput.value = `${apiBase}/`;
    form.appendChild(callbackInput);

    document.body.appendChild(form);
    form.submit();
  };

  const devLogin = async (email = 'dev@local.test') => {
    // Backend sets the cookie; we just need to refresh user state.
    await api.post('/api/dev-auth/login', { email });
    const me = await api.get('/api/me');
    setUser(me.data.user);
    return me.data.user;
  };

  const signOut = async () => {
    try {
      await api.post('/api/logout');
    } catch {
      // Ignore network errors during sign-out
    }
    setUser(null);
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{ user, loading, signInWithGoogle, devLogin, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
